import { useEffect } from "react";
import { WA_PHONE } from "./shared";

export default function SeoTags({ city, lang, t }) {
  useEffect(() => {
    if (!t) return;
    document.title = t.title;
    const set = (n, c) => {
      let el = document.querySelector(`meta[name="${n}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", n); document.head.appendChild(el); }
      el.setAttribute("content", c);
    };
    set("description", t.description);
    set("keywords", t.keywords);
    const origin = window.location.origin;
    const link = (rel, href, hreflang) => {
      const sel = `link[rel="${rel}"]${hreflang ? `[hreflang="${hreflang}"]` : ""}`;
      let el = document.querySelector(sel);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); if (hreflang) el.setAttribute("hreflang", hreflang); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    link("canonical", `${origin}/weddings/${city.slug}${lang !== "ru" ? `?lang=${lang}` : ""}`);
    const cityLangs = city.languages || ["ru", "en"];
    cityLangs.forEach((lg) => {
      link("alternate", `${origin}/weddings/${city.slug}${lg !== "ru" ? `?lang=${lg}` : ""}`, lg);
    });

    // Preload hero with responsive sources for faster LCP, especially on mobile.
    // Only for local .webp heroes (storage-hosted .jpg heroes have no -sm/-md variants).
    if (city.heroImage && /\.webp$/.test(city.heroImage)) {
      const heroBase = city.heroImage.replace(/\.webp$/, "");
      const heroSm = `${heroBase}-sm.webp`;
      const heroMd = `${heroBase}-md.webp`;
      const PRELOAD_ID = "wedding-hero-preload";
      document.getElementById(PRELOAD_ID)?.remove();
      const pre = document.createElement("link");
      pre.id = PRELOAD_ID;
      pre.rel = "preload";
      pre.as = "image";
      pre.type = "image/webp";
      pre.setAttribute("fetchpriority", "high");
      pre.setAttribute(
        "imagesrcset",
        `${heroSm} 900w, ${heroMd} 1280w, ${city.heroImage} 1700w`
      );
      pre.setAttribute("imagesizes", "100vw");
      pre.href = heroSm;
      document.head.appendChild(pre);
    }
  }, [city, lang, t]);

  if (!t) return null;
  const origin = typeof window !== "undefined" ? window.location.origin : "https://yuccaflower.com";
  const cityNameLocalized = lang === "ru" ? city.name : city.nameEn;
  const pageUrl = `${origin}/weddings/${city.slug}${lang !== "ru" ? `?lang=${lang}` : ""}`;

  const SCHEMA_T = {
    ru: { serviceType: "Свадебная флористика", home: "Главная", weddings: "Свадьбы", portfolio: (c) => `Портфолио свадеб в ${c}`, howto: (c) => `Как организовать свадьбу в ${c} с YUCCA` },
    en: { serviceType: "Wedding floral design", home: "Home", weddings: "Weddings", portfolio: (c) => `Wedding floristry portfolio in ${c}`, howto: (c) => `How to plan a wedding in ${c} with YUCCA` },
    tr: { serviceType: "Düğün çiçek tasarımı", home: "Ana sayfa", weddings: "Düğünler", portfolio: (c) => `${c} düğün çiçekçilik portföyü`, howto: (c) => `YUCCA ile ${c} düğün nasıl planlanır` },
  };
  const s = SCHEMA_T[lang] || SCHEMA_T.ru;
  const galleryImgs = (city.gallery || []).map(img => ({
    "@type": "ImageObject",
    contentUrl: `${origin}${img.src}`,
    url: `${origin}${img.src}`,
    name: (img.tag && (img.tag[lang] || img.tag.ru)) || "",
    description: (img.alt && (img.alt[lang] || img.alt.ru)) || "",
  }));
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: t.title,
        description: t.description,
        url: pageUrl,
        image: galleryImgs.length > 0 ? galleryImgs[0].contentUrl : undefined,
        provider: { "@type": "LocalBusiness", name: "YUCCA Flowers", url: "https://yuccaflower.com", telephone: `+${WA_PHONE}` },
        areaServed: { "@type": "City", name: cityNameLocalized },
        serviceType: s.serviceType,
        offers: { "@type": "AggregateOffer", priceCurrency: "EUR", lowPrice: "2500", highPrice: "15000" },
      },
      {
        "@type": "LocalBusiness",
        "@id": `${origin}/#business`,
        name: "YUCCA Flowers",
        url: "https://yuccaflower.com",
        telephone: `+${WA_PHONE}`,
        image: galleryImgs.length > 0 ? galleryImgs[0].contentUrl : `${origin}/og-image.jpg`,
        priceRange: "€€€",
        areaServed: { "@type": "City", name: cityNameLocalized },
        aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "80", bestRating: "5", worstRating: "1" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: s.home, item: `${origin}/${lang !== "ru" ? `?lang=${lang}` : ""}` },
          { "@type": "ListItem", position: 2, name: s.weddings, item: `${origin}/weddings${lang !== "ru" ? `?lang=${lang}` : ""}` },
          { "@type": "ListItem", position: 3, name: cityNameLocalized, item: pageUrl },
        ],
      },
      ...(galleryImgs.length > 0
        ? [{
            "@type": "ImageGallery",
            name: s.portfolio(cityNameLocalized),
            url: pageUrl,
            image: galleryImgs,
          }]
        : []),
      ...((t.timeline && t.timeline.length > 0)
        ? [{
            "@type": "HowTo",
            name: s.howto(cityNameLocalized),
            description: t.timelineSubtitle,
            totalTime: "P3M",
            step: t.timeline.map((s, i) => ({
              "@type": "HowToStep",
              position: i + 1,
              name: s.title,
              text: `${s.when} — ${s.text}`,
            })),
          }]
        : []),
      { "@type": "FAQPage", mainEntity: (t.faq || []).map(f => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
    ],
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />;
}
